import React, { useEffect, CSSProperties } from 'react';
import { Stack, Text } from 'rsuite';
import DoingRoundIcon from '@rsuite/icons/DoingRound';
import InfoRoundIcon from '@rsuite/icons/InfoRound';
import RemindFillIcon from '@rsuite/icons/RemindFill';
import CheckRoundIcon from '@rsuite/icons/CheckRound';
import RemindRoundIcon from '@rsuite/icons/RemindRound';
import { Color } from '../../types/colors';

const validTypes = ['loading', 'info', 'warning', 'danger', 'success'];

export enum IconIndicatorType {
  LOADING = 'loading',
  INFO = 'info',
  WARNING = 'warning',
  DANGER = 'danger',
  SUCCESS = 'success',
}

export interface IconIndicatorProps {
  type?: IconIndicatorType;
  isAnimated?: boolean;
  size?: number;
  color?: Color;
  text?: string;
  isVertical?: boolean;
  textStyle?: CSSProperties;
  customIcon?: React.ReactNode;
}

const IconIndicator = ({
  type = IconIndicatorType.LOADING,
  isAnimated = true,
  size = 24,
  color = '#ABABAB',
  text = '',
  isVertical = true,
  textStyle = { marginLeft: 8 },
  customIcon,
  ...rest
}: IconIndicatorProps) => {
  useEffect(() => {
    const prevStyle = getComputedStyle(
      document.documentElement
    ).getPropertyValue('--rs-primary-600');
    document.documentElement.style.setProperty('--rs-primary-600', '#FF0000');

    // On unmount
    return () => {
      document.documentElement.style.setProperty('--rs-primary-600', prevStyle);
    };
  }, []);

  const getTypeIcon = () => {
    const iconType = validTypes.includes(type) ? type : 'loading';
    switch (iconType) {
      default:
      case 'loading':
        return (
          <DoingRoundIcon
            aria-hidden="true"
            spin
            color={color}
            style={{
              fontSize: size,
            }}
          />
        );
      case 'info':
        return (
          <InfoRoundIcon
            aria-hidden="true"
            spin={isAnimated}
            color={color}
            style={{
              fontSize: size,
            }}
          />
        );
      case 'warning':
        return (
          <RemindRoundIcon
            aria-hidden="true"
            spin={isAnimated}
            color={color}
            style={{
              fontSize: size,
            }}
          />
        );
      case 'danger':
        return (
          <RemindFillIcon
            aria-hidden="true"
            spin={isAnimated}
            color={color}
            style={{
              fontSize: size,
            }}
          />
        );
      case 'success':
        return (
          <CheckRoundIcon
            aria-hidden="true"
            spin={isAnimated}
            color={color}
            style={{
              fontSize: size,
            }}
          />
        );
    }
  };

  const defaultAriaLabel: Record<IconIndicatorType, string> = {
    [IconIndicatorType.LOADING]: 'Loading',
    [IconIndicatorType.INFO]: 'Information',
    [IconIndicatorType.WARNING]: 'Warning',
    [IconIndicatorType.DANGER]: 'Error',
    [IconIndicatorType.SUCCESS]: 'Success',
  };

  const role = type === IconIndicatorType.DANGER ? 'alert' : 'status';

  return (
    <Stack
      {...rest}
      direction={isVertical ? 'column' : 'row'}
      style={{ justifyContent: 'center' }}
      data-testid="icon-indicator"
      role={role}
      aria-label={text.length > 0 ? text : defaultAriaLabel[type]}
    >
      <Stack.Item>{customIcon || getTypeIcon()}</Stack.Item>
      {text.length > 0 ? (
        <Stack.Item>
          <Text style={textStyle}>{text}</Text>
        </Stack.Item>
      ) : null}
    </Stack>
  );
};

export default IconIndicator;
